import React, { useState } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';

const AuthContainer = styled.div`
  min-height: 100vh;
  background: ${theme.colors.doryBeige};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing.lg};
`;

const AuthCard = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.lg};
  max-width: 400px;
  width: 100%;
`;

const AuthTitle = styled.h1`
  font-family: ${theme.typography.headerFont};
  font-size: ${theme.typography.sizes.h1};
  font-weight: ${theme.typography.weights.semiBold};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.md};
  text-align: center;
`;

const AuthSubtitle = styled.p`
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.textLight};
  margin: 0 0 ${theme.spacing.xl} 0;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: ${theme.spacing.lg};
`;

const Label = styled.label`
  display: block;
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing.md};
  border: 2px solid ${theme.colors.doryLighterBlue};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  color: ${theme.colors.text};
  transition: border-color ${theme.animation.durations.fast};
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
  }
`;

const LoginButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.accent} 100%);
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.body};
  font-weight: ${theme.typography.weights.medium};
  cursor: pointer;
  transition: all ${theme.animation.durations.fast};
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${theme.shadows.md};
  }
  
  &:disabled {
    background: ${theme.colors.disabled};
    cursor: not-allowed;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error};
  color: white;
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.bodyFont};
  font-size: ${theme.typography.sizes.small};
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const AdminAuth = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      // Get admin password from environment variable
      const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD;
      
      if (!adminPassword) {
        throw new Error('Admin password not configured');
      }

      if (password === adminPassword) {
        // Store authentication state in sessionStorage
        sessionStorage.setItem('adminAuthenticated', 'true');
        sessionStorage.setItem('adminAuthTime', Date.now().toString());
        onLogin();
      } else {
        setError('Invalid password. Please try again.');
      }
    } catch (err) {
      setError(err.message || 'Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContainer>
      <AuthCard>
        <AuthTitle>Admin Access</AuthTitle>
        <AuthSubtitle>Enter password to access the admin panel</AuthSubtitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              disabled={isLoading}
              required
            />
          </FormGroup>
          
          <LoginButton type="submit" disabled={isLoading || !password.trim()}>
            {isLoading ? 'Authenticating...' : 'Login'}
          </LoginButton>
        </form>
      </AuthCard>
    </AuthContainer>
  );
};

export default AdminAuth;
